import { loadStripe } from '@stripe/stripe-js';
import type { Appearance } from '@stripe/stripe-js';

/**
 * Module-scope loadStripe singleton — called ONCE (per-render re-fetches Stripe.js).
 * Pass to `<Elements stripe={stripePromise} options={{clientSecret, appearance}}>`.
 *
 * PUBLISHABLE (pk_*) key ONLY — never secret key on the client.
 * VITE_STRIPE_PUBLISHABLE_KEY is declared in apps/web/vite-env.d.ts.
 * Vite DCEs the env var at build time.
 */
export const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
);

/**
 * Dark-theme appearance for the embedded PaymentElement / SetupElement.
 *
 * Ported verbatim from `apps/web/src/prototypes/in-app-checkout/stripe-appearance.ts`.
 *
 * LOAD-BEARING (ADR 0007 / research §11 #1): Stripe's `variables.colorPrimary` /
 * `colorBackground` / `colorText` / `colorDanger` / `colorWarning` accept
 * LITERAL HEX ONLY — they REJECT `var(--x)` and `rgba()`. The Intelligence
 * Terminal tokens are CSS vars in `index.css` `@theme`, so the hex MUST be
 * inlined here. Every line carries the `// @design-token` annotation so
 * `audit-no-raw-hex-in-signal.sh` and reviewers know it is a deliberate exception.
 */
export const STRIPE_APPEARANCE: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#00c853', // @design-token --color-accent
    colorBackground: '#050709', // @design-token --color-input-bg
    colorText: '#c8ced8', // @design-token --color-text
    colorTextSecondary: '#586878', // @design-token --color-text-muted
    colorTextPlaceholder: '#2e3a4a', // @design-token --color-text-dim
    colorDanger: '#c03040', // @design-token --color-danger
    colorWarning: '#d4960f', // @design-token --color-warn
    colorIconTabSelected: '#00c853', // @design-token --color-accent
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    fontSizeBase: '13px',
    borderRadius: '3px',
    spacingUnit: '3px',
  },
  rules: {
    '.Input': {
      backgroundColor: '#050709', // @design-token --color-input-bg
      border: '1px solid #0f1520', // @design-token --color-panel-border
      boxShadow: 'none',
      color: '#e4e8ee', // @design-token --color-text-bright
    },
    '.Input:focus': {
      border: '1px solid #00c853', // @design-token --color-accent
      boxShadow: '0 0 0 1px #00c85344', // @design-token --color-accent-glow
    },
    '.Label': {
      color: '#586878', // @design-token --color-text-muted
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: '8px',
      textTransform: 'uppercase',
      letterSpacing: '1.4px',
    },
    '.Tab, .Block': {
      backgroundColor: '#070a0f', // @design-token --color-panel-bg
      border: '1px solid #0f1520', // @design-token --color-panel-border
    },
    '.Tab--selected': {
      border: '1px solid #00c85366', // @design-token --color-accent (40% alpha)
    },
  },
};
